import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Search, Phone, Mail, ArrowRight, AlertTriangle, RotateCcw, Zap, Globe, Users, Factory } from 'lucide-react';
import { t } from '../utils/translation';

const NotFound = ({ theme, language }) => {
  const navigate = useNavigate();
  const [suggestedPages, setSuggestedPages] = useState([]);
  
  // Suggested pages based on common user paths
  const commonPages = [
    { path: '/', label: t('returnHome', language), icon: Home },
    { path: '/products', label: t('browseProducts', language), icon: Search },
    { path: '/services', label: t('ourServices', language) || 'Our Services', icon: Zap },
    { path: '/industries', label: t('industries', language), icon: Factory },
    { path: '/contact', label: t('contact', language), icon: Mail }
  ];

  // Auto-focus on page load for accessibility
  useEffect(() => {
    const mainHeading = document.querySelector('h1');
    if (mainHeading) {
      mainHeading.focus();
    }
  }, []);

  // Get current time for personalized message
  const getCurrentTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('goodMorning', language) || 'Good morning';
    if (hour < 18) return t('goodAfternoon', language) || 'Good afternoon';
    return t('goodEvening', language) || 'Good evening';
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900'} pt-32`}>
      <Helmet>
        <title>{t('pageNotFound', language)} - Qingshun Electronics</title>
        <meta name="description" content={t('pageNotFoundText', language)} />
        <meta name="keywords" content="404, page not found, error, connector manufacturer" />
        <meta name="author" content="Qingshun Electronics Technology Co., Ltd." />
        <link rel="canonical" href={`${window.location.origin}/404`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/404`} />
        <meta property="og:title" content={`${t('pageNotFound', language)} - Qingshun Electronics`} />
        <meta property="og:description" content={t('pageNotFoundText', language)} />
        <meta property="og:image" content="/logo512.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${window.location.origin}/404`} />
        <meta property="twitter:title" content={`${t('pageNotFound', language)} - Qingshun Electronics`} />
        <meta property="twitter:description" content={t('pageNotFoundText', language)} />
        <meta property="twitter:image" content="/logo512.png" />
      </Helmet>
      
      <section className="relative py-12 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Main 404 Content */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-red-500/10 mb-8 animate-pulse">
              <AlertTriangle className="w-20 h-20 text-red-500" />
            </div>
            
            <div className="text-[120px] md:text-[180px] font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500 mb-2 animate-bounce">
              404
            </div>
            
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 transition-colors duration-300 outline-none" tabIndex="-1">
              {t('pageNotFound', language)}
            </h1>
            
            <p className={`text-xl md:text-2xl max-w-3xl mx-auto mb-10 leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('pageNotFoundText', language)}
            </p>
            
            <div className={`inline-flex items-center px-6 py-3 rounded-full mb-12 transition-colors duration-300 ${theme === 'dark' ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
              <span className="font-medium text-lg">
                {getCurrentTimeGreeting()}, {t('backToBusiness', language) || 'let\'s get you back on track with Qingshun Electronics'}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Quick Navigation */}
            <div className={`lg:col-span-2 rounded-3xl p-8 shadow-2xl transition-all duration-300 transform hover:scale-[1.02] ${theme === 'dark' ? 'bg-gradient-to-br from-gray-800 to-black border border-gray-700' : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200'}`}>
              <div className="flex items-center mb-8">
                <Globe className={`w-8 h-8 mr-3 ${theme === 'dark' ? 'text-red-500' : 'text-red-600'}`} />
                <h2 className="text-3xl font-bold transition-colors duration-300">
                  {t('quickNavigation', language) || 'Quick Navigation'}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {commonPages.map((page, index) => {
                  const IconComponent = page.icon;
                  return (
                    <Link 
                      key={index}
                      to={page.path} 
                      className={`flex items-center p-5 rounded-2xl transition-all duration-300 group transform hover:-translate-y-1 ${theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-100'}`}
                    >
                      <div className={`p-3 rounded-xl mr-5 transition-colors duration-300 ${theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100'}`}>
                        <IconComponent className="w-6 h-6 text-red-500" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-bold text-lg transition-colors duration-300">{page.label}</div>
                        <div className={`text-sm transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {t('goToPage', language) || 'Go to page'}
                        </div>
                      </div>
                      <ArrowRight className={`w-5 h-5 transition-colors duration-300 ${theme === 'dark' ? 'text-gray-500 group-hover:text-red-500' : 'text-gray-400 group-hover:text-red-600'}`} />
                    </Link>
                  );
                })}
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-700 flex flex-wrap gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className={`flex items-center px-5 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${theme === 'dark' ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  {t('goBack', language) || 'Go Back'}
                </button>
                
                <button
                  onClick={() => navigate('/')}
                  className={`flex items-center px-5 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${theme === 'dark' ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                >
                  <Home className="w-5 h-5 mr-2" />
                  {t('returnHome', language)}
                </button>
              </div>
            </div>
            
            {/* Help & Support */}
            <div className={`rounded-3xl p-8 shadow-2xl transition-all duration-300 transform hover:scale-[1.02] ${theme === 'dark' ? 'bg-gradient-to-br from-gray-800 to-black border border-gray-700' : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200'}`}>
              <div className="flex items-center mb-8">
                <Users className={`w-8 h-8 mr-3 ${theme === 'dark' ? 'text-red-500' : 'text-red-600'}`} />
                <h2 className="text-3xl font-bold transition-colors duration-300">
                  {t('needHelp404', language) || 'Need Help?'}
                </h2>
              </div>
              
              <p className={`mb-8 text-lg leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('help404', language) || 'If you\'re having trouble finding what you\'re looking for, our team is here to help.'}
              </p>
              
              <div className="space-y-5">
                <a 
                  href="/contact" 
                  className={`flex items-start p-5 rounded-2xl transition-all duration-300 group transform hover:-translate-y-1 ${theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-100'}`}
                >
                  <div className={`p-3 rounded-xl mr-5 transition-colors duration-300 ${theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100'}`}>
                    <Mail className="w-6 h-6 text-red-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-lg transition-colors duration-300">{t('contactSupport', language) || 'Contact Support'}</div>
                    <div className={`text-sm transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('getInTouch', language) || 'Get in touch with our team'}
                    </div>
                  </div>
                </a>
                
                <a 
                  href="tel:+8618013289913" 
                  className={`flex items-start p-5 rounded-2xl transition-all duration-300 group transform hover:-translate-y-1 ${theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-100'}`}
                >
                  <div className={`p-3 rounded-xl mr-5 transition-colors duration-300 ${theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100'}`}>
                    <Phone className="w-6 h-6 text-red-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-lg transition-colors duration-300">{t('callUs', language) || 'Call Us'}</div>
                    <div className={`text-sm transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      +86 18013289913
                    </div>
                  </div>
                </a>
              </div>
              
              <div className="mt-10 pt-6 border-t border-gray-700">
                <div className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
                  <h3 className="font-bold text-lg mb-3 transition-colors duration-300">
                    {t('didYouKnow', language) || 'Did You Know?'}
                  </h3>
                  <p className={`text-sm transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('errorReference', language) || 'Error code: 404 - Page not found'} • {new Date().getFullYear()}
                  </p>
                  <p className={`mt-2 text-sm transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('pageTip', language) || 'Try checking the URL for errors or use our navigation above.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Brand Message */}
          <div className="text-center mt-16 mb-12">
            <div className={`inline-flex items-center px-8 py-4 rounded-full text-xl transition-colors duration-300 ${theme === 'dark' ? 'bg-gradient-to-r from-red-900/30 to-orange-900/30 text-red-400' : 'bg-gradient-to-r from-red-100 to-orange-100 text-red-600'}`}>
              <span className="font-bold">
                {t('trustedBy', language) || 'Trusted by 1000+ global clients since 2011'}
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFound;